import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Package, Plus, Trash2, Save, CheckCircle2, AlertCircle,
  Loader2, Tag, DollarSign, Hash, Layers
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UNIT_OPTIONS = ['piece', 'm2', 'meter', 'day', 'set'];
const CURRENCY_OPTIONS = ['AED', 'USD', 'EUR', 'GBP', 'TRY'];

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

const emptyCatalogItem = () => ({
  _id: `tmp_${Date.now()}`,
  sku: '',
  name: '',
  unit: 'piece',
  price: 0,
  currency: 'USD',
  images: [],
});

export default function SupplierCatalogue() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [categories, setCategories] = useState(['General']);
  const [newCategory, setNewCategory] = useState('');
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState(emptyCatalogItem());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const { data } = await axios.get(`${API}/api/supplier/profile`, { headers: { Authorization: `Bearer ${token}` } });
        const catalog = data.profile?.catalog || {};
        setCategories(catalog.categories || ['General']);
        setItems(catalog.items || []);
      } catch {
        showToast('error', 'Failed to load catalogue');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    // Validate SKU uniqueness
    const skus = items.map(i => i.sku);
    if (new Set(skus).size !== skus.length) {
      showToast('error', 'All SKUs must be unique. Please fix duplicate SKUs.');
      return;
    }
    // Validate required fields
    for (const item of items) {
      if (!item.sku || item.sku.length < 2) { showToast('error', `SKU is required (min 2 chars) for all items.`); return; }
      if (!item.name || item.name.length < 2) { showToast('error', `Name is required (min 2 chars) for all items.`); return; }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const cleanItems = items.map(({ _id, ...rest }) => rest);
      await axios.patch(`${API}/api/supplier/profile`,
        { catalog: { categories, items: cleanItems } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('success', 'Catalogue saved successfully!');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    if (!newItem.sku || newItem.sku.length < 2) { showToast('error', 'SKU must be at least 2 characters.'); return; }
    if (!newItem.name || newItem.name.length < 2) { showToast('error', 'Name must be at least 2 characters.'); return; }
    setItems(p => [...p, { ...newItem, _id: `tmp_${Date.now()}` }]);
    setNewItem(emptyCatalogItem());
    setShowAddItem(false);
  };

  const filteredItems = activeCategory === 'All' ? items : items.filter(it => it.category === activeCategory);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin h-8 w-8 text-purple-400" />
    </div>
  );

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Catalogue</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your product categories and catalogue items</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white gap-2" data-testid="save-catalogue-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving...' : 'Save Catalogue'}
        </Button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 p-4 rounded-xl text-sm font-medium shadow ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertCircle className="h-5 w-5 text-red-500" />}
          {toast.msg}
        </div>
      )}

      {/* Categories Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="h-5 w-5 text-purple-600" />
          <h2 className="text-base font-semibold text-gray-800">Product Categories</h2>
        </div>
        <p className="text-sm text-gray-500">Add the product categories you supply. These appear as filters on your public profile.</p>
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <span key={cat} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm text-purple-700 font-medium">
              {cat}
              <button onClick={() => { if (categories.length > 1) setCategories(prev => prev.filter(c => c !== cat)); }}
                className="text-purple-400 hover:text-purple-700 transition-colors">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Add category (e.g. Exhibition Staff Uniforms)"
            value={newCategory} onChange={e => setNewCategory(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newCategory.trim() && !categories.includes(newCategory.trim())) { setCategories(p => [...p, newCategory.trim()]); setNewCategory(''); } }}
            data-testid="input-new-category" />
          <button onClick={() => { if (newCategory.trim() && !categories.includes(newCategory.trim())) { setCategories(p => [...p, newCategory.trim()]); setNewCategory(''); } }}
            disabled={!newCategory.trim()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors"
            data-testid="btn-add-category">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>

      {/* Products Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-purple-600" />
            <h2 className="text-base font-semibold text-gray-800">Catalogue Items</h2>
            <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-0.5 rounded-full">{items.length}</span>
          </div>
          <button onClick={() => setShowAddItem(p => !p)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
            data-testid="btn-add-item">
            <Plus className="h-4 w-4" /> Add Item
          </button>
        </div>

        {/* Add Item Form */}
        {showAddItem && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-purple-800">New Catalogue Item</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div><label className={labelCls}>SKU *</label><input className={inputCls} value={newItem.sku} onChange={e => setNewItem(p => ({ ...p, sku: e.target.value }))} placeholder="e.g. UNI-POLO-001" /></div>
              <div><label className={labelCls}>Product Name *</label><input className={inputCls} value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Custom Polo Shirt" /></div>
              <div>
                <label className={labelCls}>Category</label>
                <select className={inputCls} value={newItem.category || ''} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Unit</label>
                <select className={inputCls} value={newItem.unit} onChange={e => setNewItem(p => ({ ...p, unit: e.target.value }))}>
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Price</label><input type="number" className={inputCls} value={newItem.price} onChange={e => setNewItem(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} placeholder="0.00" /></div>
              <div>
                <label className={labelCls}>Currency</label>
                <select className={inputCls} value={newItem.currency} onChange={e => setNewItem(p => ({ ...p, currency: e.target.value }))}>
                  {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={addItem} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-2">
                <Plus className="h-4 w-4" /> Add to Catalogue
              </button>
              <button onClick={() => setShowAddItem(false)} className="px-5 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg text-sm font-semibold transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Category Filter */}
        {items.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {['All', ...categories].map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${activeCategory === cat ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-200 text-gray-600 hover:border-purple-300'}`}>
                {cat} {cat === 'All' ? `(${items.length})` : `(${items.filter(i => i.category === cat).length})`}
              </button>
            ))}
          </div>
        )}

        {/* Items Table */}
        {filteredItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 pr-4">SKU</th>
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 pr-4">Name</th>
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 pr-4">Category</th>
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 pr-4">Unit</th>
                  <th className="text-right text-xs font-semibold text-gray-500 py-2 pr-4">Price</th>
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 pr-4">Currency</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item._id || item.sku} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 pr-4"><span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">{item.sku}</span></td>
                    <td className="py-3 pr-4 font-medium text-gray-900">{item.name}</td>
                    <td className="py-3 pr-4"><span className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full border border-purple-100">{item.category || '—'}</span></td>
                    <td className="py-3 pr-4 text-gray-500">{item.unit}</td>
                    <td className="py-3 pr-4 text-right font-semibold text-gray-800">{item.price}</td>
                    <td className="py-3 pr-4 text-gray-500">{item.currency}</td>
                    <td className="py-3">
                      <button onClick={() => setItems(p => p.filter(i => i !== item))} className="text-red-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm font-medium">No items in this category</p>
            <p className="text-xs mt-1">Click "Add Item" to add your first product to the catalogue</p>
          </div>
        )}
      </div>
    </div>
  );
}
